import type { Lesson, LessonType } from '../../types/content'
import { concepts } from './concepts'
import { words } from './words'

const lessonTypesFor = (conceptId: string, type: string): LessonType[] => {
  if (type === 'phonemic_awareness') {
    if (conceptId.includes('segment')) return ['hear', 'segment', 'review']
    if (conceptId.includes('blend')) return ['hear', 'blend', 'review']
    return ['hear', 'recognition', 'review']
  }
  if (type === 'grapheme_phoneme_correspondence') return ['hear', 'recognition', 'sound-hunt', 'blend', 'segment', 'decode', 'encode', 'word-challenge', 'review']
  if (type === 'blend_skill') return ['blend', 'segment', 'decode', 'word-challenge', 'review']
  if (type === 'morphology') return ['hear', 'segment', 'decode', 'encode', 'review']
  return ['hear', 'recognition', 'decode', 'encode', 'review']
}

const eligibleWords = (conceptId: string) => {
  const concept = concepts.find((item) => item.id === conceptId)
  if (!concept) return []
  const introducedHere = words.filter((word) => word.introducedAtConceptId === conceptId)
  const mappingPractice = words.filter((word) =>
    !word.teacherSupported
    && word.mapping.some((mapping) => concept.mappingIds.includes(mapping.mappingId))
    && word.decodableAfterConcepts.every((requiredId) => (concepts.find((item) => item.id === requiredId)?.order ?? Number.POSITIVE_INFINITY) <= concept.order),
  )
  return Array.from(new Map([...introducedHere, ...mappingPractice].map((word) => [word.id, word])).values())
    .sort((a, b) => a.difficulty - b.difficulty || a.spelling.localeCompare(b.spelling))
    .slice(0, 12)
}

export const lessons: Lesson[] = concepts.flatMap((concept) => {
  const lessonWords = eligibleWords(concept.id)
  return lessonTypesFor(concept.id, concept.type).map((type, index) => ({
    id: `lesson_${concept.id}_${type}`,
    conceptId: concept.id,
    stage: concept.stage,
    type,
    difficulty: Math.min(5, Math.max(1, Math.ceil((concept.stage + 1) / 4))) as Lesson['difficulty'],
    targetPhonemeIds: concept.phonemeIds,
    targetGraphemeIds: concept.graphemeIds,
    targetMappingIds: concept.mappingIds,
    wordIds: (type === 'hear' || type === 'recognition' || type === 'sound-hunt') ? lessonWords.slice(0, 5).map((word) => word.id) : lessonWords.slice(index % 3, index % 3 + 6).map((word) => word.id),
    mode: concept.type === 'phonemic_awareness' ? 'oral_only' : lessonWords.some((word) => word.teacherSupported) ? 'teacher_supported' : 'decodable',
  }))
})

export const lessonById = new Map(lessons.map((lesson) => [lesson.id, lesson]))

